import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {createRef, CSSProperties, ReactNode, RefObject} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import DialogDefault from "../../parameter/PortalDialogDefault";
import UrlConverterDialogFields from "../../field/document/UrlConverterDialogFields";
import {InputText} from "primereact/inputtext";
import validator from "validator";
import {Password} from "primereact/password";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import PaperHelper, {PaperFormat, paperSizes} from "../../../../shared/helper/PaperHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {PREVIEW_SIZE} from "../../../startscreen/Preview";
import PortalDocument from "../../../document/PortalDocument";
import {PortalUrlConverterPage} from "../../../../shared/models/PortalUrlConverterPage";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {PortalUrlConverter} from "../../../../shared/models/PortalUrlConverter";
import {
	AdminWebserviceStatus,
	Environment,
	Functions,
	HttpBasicAuth,
	Information,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	UrlConverter
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import FormatEnum from "../../../../shared/models/FormatEnum";
import OrientationEnum from "../../../../shared/models/OrientationEnum";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import {TabPanel, TabView} from "primereact/tabview";
import Immutable from "../../../../shared/immutable/Immutable";
import CodeInput, {CodeInputVariable} from "../../../../shared/components/CodeInput";
import {
	HeaderFooterAlignment,
	PortalConverterHeaderFooter
} from "../../../../shared/models/PortalConverterHeaderFooter";
import DropdownFilter from "../../../../shared/components/DropdownFilter";
import DropdownNumber from "../../../../shared/components/DropdownNumber";
import {PortalConverterHeaderFooterMargins} from "../../../../shared/models/PortalConverterHeaderFooterMargins";
import {Menu} from "primereact/menu";
import {InputSwitch} from "primereact/inputswitch";
import {SelectItem} from "primereact/selectitem";
import {ColorPicker} from "primereact/colorpicker";
import {Base64} from "js-base64";

interface UrlConverterDialogProperties extends PortalDialogProperties<PortalUrlConverter> {
}

interface UrlConverterDialogStatus extends PortalDialogState<PortalUrlConverter> {
	unit: LengthUnit;
	panel: number;
}

export class UrlConverterDialogDefinition extends PortalDialog<PortalUrlConverter,
	UrlConverterDialogProperties, UrlConverterDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<UrlConverterDialogFields, string>(),
			unit: LengthUnit.MILLIMETER,
			panel: 0
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.urlconverter?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.uploadUrl, Functions.getUploadUrlDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		return this.isEnabled(portalInfo);
	}

	private renderGeneralTab(parameter: PortalUrlConverter): ReactNode {
		let errors: Map<UrlConverterDialogFields, string> = this.state.fieldErrors;

		let pageParameter: PortalUrlConverterPage | undefined = parameter?.page as PortalUrlConverterPage;

		let pageFormats: Array<any> = [
			{label: Translate.value("urlconverter.format.custom", {ns: "dialog"}), value: FormatEnum.CUSTOM}
		];

		for (let formatGroup of paperSizes) {
			pageFormats.push({
				label: Translate.value("papersize." + formatGroup.name),
				className: "group",
				value: formatGroup.name,
				disabled: true
			});

			for (let paperFormat of formatGroup.values) {
				pageFormats.push({label: paperFormat.name, className: "item", value: paperFormat.name});
			}
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(UrlConverterDialogFields.URL)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-url">
							<Translate {...this.props} data="urlconverter.general.url" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-url"
									   className={errors.has(UrlConverterDialogFields.URL) ? "p-invalid" : ""}
									   tooltip={errors.has(UrlConverterDialogFields.URL) ? Translate.value(
										   errors.get(UrlConverterDialogFields.URL)!, {ns: "message"}
									   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.url, UrlConverter.getUrlDefault()
									   )}
									   onInput={(e) => this.onChange(
										   UrlConverterDialogFields.URL, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(UrlConverterDialogFields.URL) ? Translate.value(
								errors.get(UrlConverterDialogFields.URL)!, {ns: "message"}
							) : "")}
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(UrlConverterDialogFields.USERNAME)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-username">
							<Translate {...this.props} data="urlconverter.general.user" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-username"
									   value={FunctionHelper.applyValue(
										   parameter?.basicAuth?.userName, HttpBasicAuth.getUserNameDefault()
									   )}
									   onInput={(e) => this.onChange(
										   UrlConverterDialogFields.USERNAME, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(UrlConverterDialogFields.PASSWORD)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-password">
							<Translate {...this.props} data="urlconverter.general.password" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Password id="control-password" feedback={false} toggleMask={true}
									  appendTo={document.getElementById("root")}
									  onInput={(e) => this.onChange(
										  UrlConverterDialogFields.PASSWORD, e.currentTarget.value
									  )}/>
						</div>
					</div>
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.urlconverter.credentials"} namespace={"help"}
								   markdown={true}/>
					</Helper>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(UrlConverterDialogFields.FORMAT)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-format">
							<Translate {...this.props} data="urlconverter.format.size" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-format"
									  value={FunctionHelper.applyValue(
										  pageParameter.format, PortalUrlConverterPage.getFormatDefault()
									  )}
									  options={pageFormats} appendTo={document.getElementById("root")}
									  onChange={(e) => this.onChange(
										  UrlConverterDialogFields.FORMAT, e.value
									  )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						pageParameter.format !== FormatEnum.CUSTOM ||
						!this.state.fields?.contains(UrlConverterDialogFields.WIDTH)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-width">
							<Translate {...this.props} data="urlconverter.format.width" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7"
							 hidden={!this.state.fields?.contains(UrlConverterDialogFields.WIDTH)}>
							<InputNumber inputId="control-width" step={1} suffix={" " + this.state.unit}
										 maxFractionDigits={2} minFractionDigits={2} min={0} showButtons={true}
										 useGrouping={false}
										 value={UnitHelper.unitToUnit(
											 FunctionHelper.applyValue(
												 pageParameter.width, PortalUrlConverterPage.getWidthDefault()
											 ), LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
										 )}
										 onChange={(e) => this.onChange(
											 UrlConverterDialogFields.WIDTH, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						pageParameter.format !== FormatEnum.CUSTOM ||
						!this.state.fields?.contains(UrlConverterDialogFields.HEIGHT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-height">
							<Translate {...this.props} data="urlconverter.format.height" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7"
							 hidden={!this.state.fields?.contains(UrlConverterDialogFields.HEIGHT)}>
							<InputNumber inputId="control-height" step={1} suffix={" " + this.state.unit}
										 maxFractionDigits={2} minFractionDigits={2} min={0} showButtons={true}
										 useGrouping={false}
										 value={UnitHelper.unitToUnit(
											 FunctionHelper.applyValue(
												 pageParameter.height, PortalUrlConverterPage.getHeightDefault()
											 ), LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
										 )}
										 onChange={(e) => this.onChange(
											 UrlConverterDialogFields.HEIGHT, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 pageParameter.format === FormatEnum.CUSTOM ||
							 !this.state.fields?.contains(UrlConverterDialogFields.ORIENTATION)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-orientation">
							<Translate {...this.props} data="urlconverter.format.orientation.label"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-orientation"
									  value={FunctionHelper.applyValue(
										  pageParameter.orientation, PortalUrlConverterPage.getOrientationDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "urlconverter.format.orientation.vertical",
												  {ns: "dialog"}
											  ),
											  value: OrientationEnum.VERTICAL
										  },
										  {
											  label: Translate.value(
												  "urlconverter.format.orientation.horizontal",
												  {ns: "dialog"}
											  ),
											  value: OrientationEnum.HORIZONTAL
										  }
									  ]}
									  appendTo={document.getElementById("root")}
									  onChange={(e) => this.onChange(
										  UrlConverterDialogFields.ORIENTATION, e.value
									  )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.MARGIN_TOP)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-margin-top">
							<Translate {...this.props} data="urlconverter.margin.top" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber maxFractionDigits={2} minFractionDigits={2} min={0} showButtons={true}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 pageParameter.top, PortalUrlConverterPage.getTopDefault()
												 ), LengthUnit.MILLIMETER, this.state.unit,
												 this.props.portalResolution
											 )} inputId="control-margin-top" step={1} suffix={" " + this.state.unit}
											 onChange={(e) => this.onChange(
												 UrlConverterDialogFields.MARGIN_TOP, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close"
											tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												UrlConverterDialogFields.MARGIN_TOP, PortalUrlConverterPage.getTopDefault()
											)}/>
								</div>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.MARGIN_BOTTOM)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-margin-bottom">
							<Translate {...this.props} data="urlconverter.margin.bottom" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-margin-bottom" step={1} useGrouping={false}
											 maxFractionDigits={2} minFractionDigits={2} min={0} showButtons={true}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 pageParameter.bottom, PortalUrlConverterPage.getBottomDefault()
												 ), LengthUnit.MILLIMETER, this.state.unit,
												 this.props.portalResolution
											 )}
											 suffix={" " + this.state.unit}
											 onChange={(e) => this.onChange(
												 UrlConverterDialogFields.MARGIN_BOTTOM, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close"
											tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												UrlConverterDialogFields.MARGIN_BOTTOM,
												PortalUrlConverterPage.getBottomDefault()
											)}/>
								</div>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.MARGIN_LEFT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-margin-left">
							<Translate {...this.props} data="urlconverter.margin.left" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-margin-left" step={1} useGrouping={false}
											 maxFractionDigits={2} minFractionDigits={2} min={0} showButtons={true}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 pageParameter.left, PortalUrlConverterPage.getLeftDefault()
												 ), LengthUnit.MILLIMETER, this.state.unit,
												 this.props.portalResolution
											 )}
											 suffix={" " + this.state.unit}
											 onChange={(e) => this.onChange(
												 UrlConverterDialogFields.MARGIN_LEFT, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close"
											tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												UrlConverterDialogFields.MARGIN_LEFT, PortalUrlConverterPage.getLeftDefault()
											)}/>
								</div>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.MARGIN_RIGHT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-margin-right">
							<Translate {...this.props} data="urlconverter.margin.right" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-margin-right" step={1} maxFractionDigits={2}
											 minFractionDigits={2} min={0} showButtons={true} useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 pageParameter.right, PortalUrlConverterPage.getRightDefault()
												 ), LengthUnit.MILLIMETER, this.state.unit,
												 this.props.portalResolution
											 )}
											 suffix={" " + this.state.unit}
											 onChange={(e) => this.onChange(
												 UrlConverterDialogFields.MARGIN_RIGHT, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close"
											tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												UrlConverterDialogFields.MARGIN_RIGHT,
												PortalUrlConverterPage.getRightDefault()
											)}/>
								</div>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.UNIT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-unit">
							<Translate {...this.props} data="urlconverter.unit" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<Dropdown inputId="control-unit"
									  value={FunctionHelper.applyValue(
										  pageParameter.unit, PortalUrlConverterPage.getUnitDefault()
									  )}
									  options={[
										  {label: Translate.value("inputs.unit.pt"), value: LengthUnit.POINTS},
										  {label: Translate.value("inputs.unit.pc"), value: LengthUnit.PICA},
										  {
											  label: Translate.value("inputs.unit.mm"),
											  value: LengthUnit.MILLIMETER
										  },
										  {label: Translate.value("inputs.unit.in"), value: LengthUnit.INCHES},
									  ]}
									  onChange={(e) => this.onChange(
										  UrlConverterDialogFields.UNIT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.urlconverter.dimensions"} namespace={"help"}
								   markdown={true}/>
					</Helper>
				</fieldset>
			</>
		);
	}

	private renderHeaderTab(parameter: PortalUrlConverter): ReactNode {
		let errors: Map<UrlConverterDialogFields, string> = this.state.fieldErrors;
		let menu: RefObject<Menu> = createRef<Menu>();

		const insertVariables: Immutable<Array<CodeInputVariable>> = Immutable.from([
			{
				label: Translate.value(
					"urlconverter.template.variables.pagenumber", {ns: "dialog"}
				),
				/* eslint-disable no-template-curly-in-string */
				value: "${html.pageNumber}"
			},
			{
				label: Translate.value(
					"urlconverter.template.variables.totalpages", {ns: "dialog"}
				),
				/* eslint-disable no-template-curly-in-string */
				value: "${html.totalPages}"
			},
			{
				label: Translate.value(
					"urlconverter.template.variables.pagetitle", {ns: "dialog"}
				),
				/* eslint-disable no-template-curly-in-string */
				value: "${html.title}"
			}
		]);

		let fonts: Array<SelectItem> = [];
		for (let font of FunctionHelper.applyValue(
			this.props.context.info.environment?.fonts, Environment.getFontsDefault()
		)) {
			fonts.push({
				label: font,
				value: font
			});
		}

		let content = FunctionHelper.applyValue(
			parameter.header?.content, PortalConverterHeaderFooter.getContentDefault()
		);

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.HEADER_PLAIN)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-plain">
							<Translate {...this.props} data="urlconverter.template.plain" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-header-plain"
										 checked={!FunctionHelper.applyValue(
											 parameter.header?.plain, PortalConverterHeaderFooter.getPlainDefault()
										 )}
										 onChange={(e) => this.onChange(
											 UrlConverterDialogFields.HEADER_PLAIN, e.value
										 )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={!FunctionHelper.applyValue(
					parameter.header?.plain, PortalConverterHeaderFooter.getPlainDefault()
				)}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(UrlConverterDialogFields.HEADER_FONT_FAMILY)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-font-family">
							<Translate {...this.props} data="urlconverter.template.font.family" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<DropdownFilter inputId="control-header-font-family"
											value={FunctionHelper.applyValue(
												parameter.header?.fontFamily,
												PortalConverterHeaderFooter.getFontFamilyDefault()
											)}
											onChange={(e) => this.onChange(
												UrlConverterDialogFields.HEADER_FONT_FAMILY, e.value
											)}
											onClear={() => this.onChange(
												UrlConverterDialogFields.HEADER_FONT_FAMILY, ""
											)}
											appendTo={document.getElementById("root")} options={fonts}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(UrlConverterDialogFields.HEADER_FONT_COLOR)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-font-color">
							<Translate {...this.props} data="urlconverter.template.font.color" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className={"p-inputgroup" + (
								errors.has(UrlConverterDialogFields.HEADER_FONT_COLOR) ? " p-invalid" : ""
							)}>
								<InputText
									className={errors.has(
										UrlConverterDialogFields.HEADER_FONT_COLOR
									) ? "p-invalid" : ""}
									tooltip={errors.has(
										UrlConverterDialogFields.HEADER_FONT_COLOR
									) ? Translate.value(
										errors.get(UrlConverterDialogFields.HEADER_FONT_COLOR)!,
										{ns: "message"}
									) : ""}
									value={FunctionHelper.applyValue(
										parameter.header?.color, PortalConverterHeaderFooter.getColorDefault()
									)}
									onInput={(e) => this.onChange(
										UrlConverterDialogFields.HEADER_FONT_COLOR, e.currentTarget.value
									)}/>
								<ColorPicker inputId="control-header-font-color" className="p-inputgroup-addon"
											 format="hex"
											 defaultColor={PortalConverterHeaderFooter.getColorDefault().replace(
												 "#", ""
											 )}
											 value={FunctionHelper.applyValue(
												 parameter.header?.color, PortalConverterHeaderFooter.getColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 UrlConverterDialogFields.HEADER_FONT_COLOR, "#" + e.value
											 )} appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(UrlConverterDialogFields.HEADER_FONT_COLOR) ?
								Translate.value(
									errors.get(UrlConverterDialogFields.HEADER_FONT_COLOR)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(UrlConverterDialogFields.HEADER_FONT_SIZE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-font-size">
							<Translate {...this.props} data="urlconverter.template.font.size" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<DropdownNumber inputId="control-header-font-size" showButtons={true}
											value={FunctionHelper.applyValue(
												parameter.header?.fontSize,
												PortalConverterHeaderFooter.getFontSizeDefault()
											)}
											onChange={(e) => this.onChange(
												UrlConverterDialogFields.HEADER_FONT_SIZE, e.value
											)}
											min={PortalConverterHeaderFooter.getFontSizeMin()}
											max={PortalConverterHeaderFooter.getFontSizeMax()}
											appendTo={document.getElementById("root")}
											options={[
												{label: "8", value: 8},
												{label: "9", value: 9},
												{label: "10", value: 10},
												{label: "11", value: 11},
												{label: "12", value: 12},
												{label: "14", value: 14},
												{label: "16", value: 16},
												{label: "18", value: 18},
												{label: "20", value: 20},
												{label: "24", value: 24},
												{label: "28", value: 28},
												{label: "36", value: 36},
												{label: "48", value: 48},
												{label: "72", value: 72}
											]}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.HEADER_ALIGNMENT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-alignment">
							<Translate {...this.props} data="urlconverter.template.font.alignment.label"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<Dropdown inputId="control-header-alignment"
									  value={FunctionHelper.applyValue(
										  parameter.header?.alignment, PortalConverterHeaderFooter.getAlignmentDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "urlconverter.template.font.alignment.left", {ns: "dialog"}
											  ),
											  value: HeaderFooterAlignment.LEFT
										  },
										  {
											  label: Translate.value(
												  "urlconverter.template.font.alignment.center", {ns: "dialog"}
											  ),
											  value: HeaderFooterAlignment.CENTER
										  },
										  {
											  label: Translate.value(
												  "urlconverter.template.font.alignment.right", {ns: "dialog"}
											  ),
											  value: HeaderFooterAlignment.RIGHT
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  UrlConverterDialogFields.HEADER_ALIGNMENT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={!FunctionHelper.applyValue(
					parameter.header?.plain, PortalConverterHeaderFooter.getPlainDefault()
				)}>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.HEADER_MARGIN_LEFT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-margin-left">
							<Translate {...this.props} data="urlconverter.template.margin.left" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-header-margin-left" step={1} showButtons={true}
											 suffix={" " + this.state.unit} maxFractionDigits={2} minFractionDigits={2}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter.header?.margins?.left,
													 PortalConverterHeaderFooterMargins.getLeftDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 UrlConverterDialogFields.HEADER_MARGIN_LEFT, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												UrlConverterDialogFields.HEADER_MARGIN_LEFT, 0
											)}/>
								</div>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.HEADER_MARGIN_RIGHT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-margin-right">
							<Translate {...this.props} data="urlconverter.template.margin.right" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-header-margin-right" step={1} showButtons={true}
											 suffix={" " + this.state.unit} maxFractionDigits={2} minFractionDigits={2}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter.header?.margins?.right,
													 PortalConverterHeaderFooterMargins.getRightDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 UrlConverterDialogFields.HEADER_MARGIN_RIGHT, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												UrlConverterDialogFields.HEADER_MARGIN_RIGHT, 0
											)}/>
								</div>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.HEADER_MARGIN_TOP)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-margin-top">
							<Translate {...this.props} data="urlconverter.template.margin.top" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-header-margin-top" step={1} showButtons={true}
											 suffix={" " + this.state.unit} maxFractionDigits={2} minFractionDigits={2}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter.header?.margins?.top,
													 PortalConverterHeaderFooterMargins.getTopDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 UrlConverterDialogFields.HEADER_MARGIN_TOP, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												UrlConverterDialogFields.HEADER_MARGIN_TOP, 0
											)}/>
								</div>
							</div>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={!FunctionHelper.applyValue(
					parameter.header?.plain, PortalConverterHeaderFooter.getPlainDefault()
				)}>
					<div className="p-col-12 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.HEADER_CONTENT)
					}>
						<label className="p-col-12 large-label" htmlFor="control-header-content">
							<Translate {...this.props} data="urlconverter.template.content" namespace="dialog"/>
						</label>
						<div className="p-col-12 large-input p-grid">
							<div className="p-inputgroup">
								<InputText id="control-header-content"
										   value={FunctionHelper.applyValue(
											   parameter.header?.content,
											   PortalConverterHeaderFooter.getContentDefault()
										   )}
										   onInput={(e) => this.onChange(
											   UrlConverterDialogFields.HEADER_CONTENT, e.currentTarget.value
										   )}/>
								<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
										onClick={() => this.onChange(
											UrlConverterDialogFields.HEADER_CONTENT, ""
										)}/>
								<Button icon="icon icon-angle-down" aria-controls={"control-header-content-menu"}
										onClick={(event) => menu.current!.toggle(event)}
										tooltip={Translate.value("inputs.code.variables")}
										className="p-inputgroup-addon" aria-haspopup={true}/>
								<Menu id="control-header-content-menu" popup={true} ref={menu}
									  appendTo={document.getElementById("root")}
									  model={[
										  {
											  label: Translate.value(
												  "urlconverter.template.variables.pagenumber", {ns: "dialog"}
											  ),
											  command: (e) => this.onChange(
												  UrlConverterDialogFields.HEADER_CONTENT,
												  content + "${html.pageNumber}"
											  )
										  },
										  {
											  label: Translate.value(
												  "urlconverter.template.variables.totalpages", {ns: "dialog"}
											  ),
											  command: (e) => this.onChange(
												  UrlConverterDialogFields.HEADER_CONTENT,
												  content + "${html.totalPages}"
											  )
										  },
										  {
											  label: Translate.value(
												  "urlconverter.template.variables.pagetitle", {ns: "dialog"}
											  ),
											  command: (e) => this.onChange(
												  UrlConverterDialogFields.HEADER_CONTENT,
												  content + "${html.title}"
											  )
										  }
									  ]}/>
							</div>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={FunctionHelper.applyValue(
					parameter.header?.plain, PortalConverterHeaderFooter.getPlainDefault()
				)}>
					<div className="p-col-12 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.HEADER_CONTENT)
					}>
						<label className="p-col-12 label-left" htmlFor="control-header-html">
							<Translate {...this.props} data="urlconverter.template.content" namespace="dialog"/>
						</label>
						<div className="p-col-12">
							<CodeInput inputId="control-header-html" language={"html"} accept={".html"} height={"400px"}
									   value={FunctionHelper.applyValue(
										   parameter.header?.content, PortalConverterHeaderFooter.getContentDefault()
									   )}
									   variables={insertVariables}
									   options={{
										   contextmenu: false,
										   minimap: {
											   enabled: false
										   }
									   }}
									   onChange={(e) => this.onChange(
										   UrlConverterDialogFields.HEADER_CONTENT, e.value
									   )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	private renderFooterTab(parameter: PortalUrlConverter): ReactNode {
		let errors: Map<UrlConverterDialogFields, string> = this.state.fieldErrors;
		let menu: RefObject<Menu> = createRef<Menu>();

		const insertVariables: Immutable<Array<CodeInputVariable>> = Immutable.from([
			{
				label: Translate.value(
					"urlconverter.template.variables.pagenumber", {ns: "dialog"}
				),
				/* eslint-disable no-template-curly-in-string */
				value: "${html.pageNumber}"
			},
			{
				label: Translate.value(
					"urlconverter.template.variables.totalpages", {ns: "dialog"}
				),
				/* eslint-disable no-template-curly-in-string */
				value: "${html.totalPages}"
			},
			{
				label: Translate.value(
					"urlconverter.template.variables.pagetitle", {ns: "dialog"}
				),
				/* eslint-disable no-template-curly-in-string */
				value: "${html.title}"
			}
		]);

		let fonts: Array<SelectItem> = [];
		for (let font of FunctionHelper.applyValue(
			this.props.context.info.environment?.fonts, Environment.getFontsDefault()
		)) {
			fonts.push({
				label: font,
				value: font
			});
		}

		let content = FunctionHelper.applyValue(
			parameter.footer?.content, PortalConverterHeaderFooter.getContentDefault()
		);

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.FOOTER_PLAIN)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-plain">
							<Translate {...this.props} data="urlconverter.template.plain" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-footer-plain"
										 checked={!FunctionHelper.applyValue(
											 parameter.footer?.plain, PortalConverterHeaderFooter.getPlainDefault()
										 )}
										 onChange={(e) => this.onChange(
											 UrlConverterDialogFields.FOOTER_PLAIN, e.value
										 )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={!FunctionHelper.applyValue(
					parameter.footer?.plain, PortalConverterHeaderFooter.getPlainDefault()
				)}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(UrlConverterDialogFields.FOOTER_FONT_FAMILY)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-font-family">
							<Translate {...this.props} data="urlconverter.template.font.family" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<DropdownFilter inputId="control-footer-font-family"
											value={FunctionHelper.applyValue(
												parameter.footer?.fontFamily,
												PortalConverterHeaderFooter.getFontFamilyDefault()
											)}
											onChange={(e) => this.onChange(
												UrlConverterDialogFields.FOOTER_FONT_FAMILY, e.value
											)}
											onClear={() => this.onChange(
												UrlConverterDialogFields.FOOTER_FONT_FAMILY, ""
											)}
											appendTo={document.getElementById("root")} options={fonts}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(UrlConverterDialogFields.FOOTER_FONT_COLOR)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-font-color">
							<Translate {...this.props} data="urlconverter.template.font.color" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className={"p-inputgroup" + (
								errors.has(UrlConverterDialogFields.FOOTER_FONT_COLOR) ? " p-invalid" : ""
							)}>
								<InputText
									className={errors.has(
										UrlConverterDialogFields.FOOTER_FONT_COLOR
									) ? "p-invalid" : ""}
									tooltip={errors.has(
										UrlConverterDialogFields.FOOTER_FONT_COLOR
									) ? Translate.value(
										errors.get(UrlConverterDialogFields.FOOTER_FONT_COLOR)!,
										{ns: "message"}
									) : ""}
									value={FunctionHelper.applyValue(
										parameter.footer?.color, PortalConverterHeaderFooter.getColorDefault()
									)}
									onInput={(e) => this.onChange(
										UrlConverterDialogFields.FOOTER_FONT_COLOR, e.currentTarget.value
									)}/>
								<ColorPicker inputId="control-footer-font-color" className="p-inputgroup-addon"
											 format="hex"
											 defaultColor={PortalConverterHeaderFooter.getColorDefault().replace(
												 "#", ""
											 )}
											 value={FunctionHelper.applyValue(
												 parameter.footer?.color, PortalConverterHeaderFooter.getColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 UrlConverterDialogFields.FOOTER_FONT_COLOR, "#" + e.value
											 )} appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(UrlConverterDialogFields.FOOTER_FONT_COLOR) ?
								Translate.value(
									errors.get(UrlConverterDialogFields.FOOTER_FONT_COLOR)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(UrlConverterDialogFields.FOOTER_FONT_SIZE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-font-size">
							<Translate {...this.props} data="urlconverter.template.font.size" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<DropdownNumber inputId="control-footer-font-size" showButtons={true}
											value={FunctionHelper.applyValue(
												parameter.footer?.fontSize,
												PortalConverterHeaderFooter.getFontSizeDefault()
											)}
											onChange={(e) => this.onChange(
												UrlConverterDialogFields.FOOTER_FONT_SIZE, e.value
											)}
											min={PortalConverterHeaderFooter.getFontSizeMin()}
											max={PortalConverterHeaderFooter.getFontSizeMax()}
											appendTo={document.getElementById("root")}
											options={[
												{label: "8", value: 8},
												{label: "9", value: 9},
												{label: "10", value: 10},
												{label: "11", value: 11},
												{label: "12", value: 12},
												{label: "14", value: 14},
												{label: "16", value: 16},
												{label: "18", value: 18},
												{label: "20", value: 20},
												{label: "24", value: 24},
												{label: "28", value: 28},
												{label: "36", value: 36},
												{label: "48", value: 48},
												{label: "72", value: 72}
											]}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.FOOTER_ALIGNMENT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-alignment">
							<Translate {...this.props} data="urlconverter.template.font.alignment.label"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<Dropdown inputId="control-footer-alignment"
									  value={FunctionHelper.applyValue(
										  parameter.footer?.alignment, PortalConverterHeaderFooter.getAlignmentDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "urlconverter.template.font.alignment.left", {ns: "dialog"}
											  ),
											  value: HeaderFooterAlignment.LEFT
										  },
										  {
											  label: Translate.value(
												  "urlconverter.template.font.alignment.center", {ns: "dialog"}
											  ),
											  value: HeaderFooterAlignment.CENTER
										  },
										  {
											  label: Translate.value(
												  "urlconverter.template.font.alignment.right", {ns: "dialog"}
											  ),
											  value: HeaderFooterAlignment.RIGHT
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  UrlConverterDialogFields.FOOTER_ALIGNMENT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={!FunctionHelper.applyValue(
					parameter.footer?.plain, PortalConverterHeaderFooter.getPlainDefault()
				)}>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.FOOTER_MARGIN_LEFT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-margin-left">
							<Translate {...this.props} data="urlconverter.template.margin.left" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-footer-margin-left" step={1} showButtons={true}
											 suffix={" " + this.state.unit} maxFractionDigits={2} minFractionDigits={2}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter.footer?.margins?.left,
													 PortalConverterHeaderFooterMargins.getLeftDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 UrlConverterDialogFields.FOOTER_MARGIN_LEFT, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												UrlConverterDialogFields.FOOTER_MARGIN_LEFT, 0
											)}/>
								</div>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.FOOTER_MARGIN_RIGHT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-margin-right">
							<Translate {...this.props} data="urlconverter.template.margin.right" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-footer-margin-right" step={1} showButtons={true}
											 suffix={" " + this.state.unit} maxFractionDigits={2} minFractionDigits={2}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter.footer?.margins?.right,
													 PortalConverterHeaderFooterMargins.getRightDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 UrlConverterDialogFields.FOOTER_MARGIN_RIGHT, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												UrlConverterDialogFields.FOOTER_MARGIN_RIGHT, 0
											)}/>
								</div>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.FOOTER_MARGIN_BOTTOM)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-margin-bottom">
							<Translate {...this.props} data="urlconverter.template.margin.bottom" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-footer-margin-bottom" step={1} showButtons={true}
											 suffix={" " + this.state.unit} maxFractionDigits={2} minFractionDigits={2}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter.footer?.margins?.bottom,
													 PortalConverterHeaderFooterMargins.getBottomDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 UrlConverterDialogFields.FOOTER_MARGIN_BOTTOM, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												UrlConverterDialogFields.FOOTER_MARGIN_BOTTOM, 0
											)}/>
								</div>
							</div>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={!FunctionHelper.applyValue(
					parameter.footer?.plain, PortalConverterHeaderFooter.getPlainDefault()
				)}>
					<div className="p-col-12 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.FOOTER_CONTENT)
					}>
						<label className="p-col-12 large-label" htmlFor="control-footer-content">
							<Translate {...this.props} data="urlconverter.template.content" namespace="dialog"/>
						</label>
						<div className="p-col-12 large-input p-grid">
							<div className="p-inputgroup">
								<InputText id="control-footer-content"
										   value={FunctionHelper.applyValue(
											   parameter.footer?.content,
											   PortalConverterHeaderFooter.getContentDefault()
										   )}
										   onInput={(e) => this.onChange(
											   UrlConverterDialogFields.FOOTER_CONTENT, e.currentTarget.value
										   )}/>
								<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
										onClick={() => this.onChange(
											UrlConverterDialogFields.FOOTER_CONTENT, ""
										)}/>
								<Button icon="icon icon-angle-down" aria-controls={"control-footer-content-menu"}
										onClick={(event) => menu.current!.toggle(event)}
										tooltip={Translate.value("inputs.code.variables")}
										className="p-inputgroup-addon" aria-haspopup={true}/>
								<Menu id="control-footer-content-menu" popup={true} ref={menu}
									  appendTo={document.getElementById("root")}
									  model={[
										  {
											  label: Translate.value(
												  "urlconverter.template.variables.pagenumber", {ns: "dialog"}
											  ),
											  command: (e) => this.onChange(
												  UrlConverterDialogFields.FOOTER_CONTENT,
												  content + "${html.pageNumber}"
											  )
										  },
										  {
											  label: Translate.value(
												  "urlconverter.template.variables.totalpages", {ns: "dialog"}
											  ),
											  command: (e) => this.onChange(
												  UrlConverterDialogFields.FOOTER_CONTENT,
												  content + "${html.totalPages}"
											  )
										  },
										  {
											  label: Translate.value(
												  "urlconverter.template.variables.pagetitle", {ns: "dialog"}
											  ),
											  command: (e) => this.onChange(
												  UrlConverterDialogFields.FOOTER_CONTENT,
												  content + "${html.title}"
											  )
										  }
									  ]}/>
							</div>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={FunctionHelper.applyValue(
					parameter.footer?.plain, PortalConverterHeaderFooter.getPlainDefault()
				)}>
					<div className="p-col-12 p-grid" hidden={
						!this.state.fields?.contains(UrlConverterDialogFields.FOOTER_CONTENT)
					}>
						<label className="p-col-12 label-left" htmlFor="control-footer-html">
							<Translate {...this.props} data="urlconverter.template.content" namespace="dialog"/>
						</label>
						<div className="p-col-12">
							<CodeInput inputId="control-footer-html" language={"html"} accept={".html"} height={"400px"}
									   value={FunctionHelper.applyValue(
										   parameter.footer?.content, PortalConverterHeaderFooter.getContentDefault()
									   )}
									   variables={insertVariables}
									   options={{
										   contextmenu: false,
										   minimap: {
											   enabled: false
										   }
									   }}
									   onChange={(e) => this.onChange(
										   UrlConverterDialogFields.FOOTER_CONTENT, e.value
									   )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderContents(): ReactNode {
		let parameter: PortalUrlConverter | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let pageParameter: PortalUrlConverterPage | undefined = parameter?.page as PortalUrlConverterPage;

		return (
			<>
				<div className="p-col-12 p-xl-8 p-grid divider-right-hidden">
					<TabView activeIndex={this.state.panel} onTabChange={
						(e) => this.setState({panel: e.index})
					}>
						<TabPanel header={Translate.value("urlconverter.general.label", {ns: "dialog"})}>
							{this.renderGeneralTab(parameter!)}
						</TabPanel>
						<TabPanel header={Translate.value("urlconverter.template.header", {ns: "dialog"})}>
							{this.renderHeaderTab(parameter!)}
						</TabPanel>
						<TabPanel header={Translate.value("urlconverter.template.footer", {ns: "dialog"})}>
							{this.renderFooterTab(parameter!)}
						</TabPanel>
					</TabView>
				</div>
				{this.renderPreview(pageParameter)}
			</>
		);
	}

	public renderPreview(parameter: PortalUrlConverterPage): ReactNode {
		let scale: number = Math.max(parameter.width!, parameter.height!) / PREVIEW_SIZE;

		let pageStyle: CSSProperties = {
			width: (parameter.width! / scale) + LengthUnit.PIXEL,
			height: (parameter.height! / scale) + LengthUnit.PIXEL
		};

		return (
			<div className="p-col-12 p-xl-4 p-lg-hidden p-grid divider-left">
				<fieldset className="p-col-12 p-grid"
						  hidden={!this.state.fields?.contains(UrlConverterDialogFields.PREVIEW)}>
					<div className="p-col-12 p-grid">
						<div className="page-preview">
							<div style={pageStyle}/>
						</div>
					</div>
				</fieldset>
			</div>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["urlconverter-dialog", "verylarge"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")} resizable={false}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.urlconverter.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.urlconverter.info"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	private convertContents(parameter: PortalConverterHeaderFooter): string {
		if (!parameter.plain) {
			return "";
		}

		let style: Array<string> = [
			"font-size:" + parameter.fontSize! + LengthUnit.POINTS,
			"padding-top:" + UnitHelper.unitToUnit(
				FunctionHelper.applyValue(parameter.margins!.top, PortalConverterHeaderFooterMargins.getTopDefault()),
				LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
			) + this.state.unit,
			"padding-bottom:" + UnitHelper.unitToUnit(
				FunctionHelper.applyValue(parameter.margins!.bottom, PortalConverterHeaderFooterMargins.getBottomDefault()),
				LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
			) + this.state.unit,
			"padding-left:" + UnitHelper.unitToUnit(
				FunctionHelper.applyValue(parameter.margins!.left, PortalConverterHeaderFooterMargins.getLeftDefault()),
				LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
			) + this.state.unit,
			"padding-right:" + UnitHelper.unitToUnit(
				FunctionHelper.applyValue(parameter.margins!.right, PortalConverterHeaderFooterMargins.getRightDefault()),
				LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
			) + this.state.unit,
			"text-align:" + parameter.alignment,
			"color:" + parameter.color,
			"width:100%"
		];

		if (!FunctionHelper.isEmpty(parameter.fontFamily)) {
			style.push("font-family:" + parameter.fontFamily!);
		}

		return '<span style="' + style.join(";") + ';">' + parameter.content + '</span>';
	}

	protected onChange(field: UrlConverterDialogFields, value: any): void {
		let parameter: PortalUrlConverter = this.state.parameter!.data;
		let pageParameter: PortalUrlConverterPage = parameter.page as PortalUrlConverterPage;
		let errors: Map<UrlConverterDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case UrlConverterDialogFields.URL:
				if (!FunctionHelper.isEmpty(value) && FunctionHelper.exists(value) && !validator.isURL(value)) {
					errors.set(field, "errors.fields.url.syntax");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.url = value;
				break;
			case UrlConverterDialogFields.USERNAME:
				parameter.basicAuth!.userName = value;
				break;
			case UrlConverterDialogFields.PASSWORD:
				parameter.basicAuth!.password = value;
				break;
			case UrlConverterDialogFields.FORMAT:
				pageParameter.format = value;

				let format: PaperFormat | null = PaperHelper.findFormat(value);
				if (!FunctionHelper.exists(format)) {
					break;
				}

				let width: number = pageParameter.orientation === OrientationEnum.VERTICAL ?
					format!.width : format!.height;
				let height: number = pageParameter.orientation === OrientationEnum.VERTICAL ?
					format!.height : format!.width;
				pageParameter.width = UnitHelper.unitToUnit(
					width, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				pageParameter.height = UnitHelper.unitToUnit(
					height, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case UrlConverterDialogFields.ORIENTATION:
				if (pageParameter.orientation === value) {
					break;
				}

				pageParameter.orientation = value;
				let tempSize: number = FunctionHelper.applyValue(pageParameter.width, PortalUrlConverterPage.getWidthDefault());
				pageParameter.width = FunctionHelper.applyValue(pageParameter.height, PortalUrlConverterPage.getHeightDefault());
				pageParameter.height = tempSize;
				break;
			case UrlConverterDialogFields.HEIGHT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalUrlConverterPage.getHeightDefault();
				}

				pageParameter.height = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case UrlConverterDialogFields.WIDTH:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalUrlConverterPage.getWidthDefault();
				}

				pageParameter.width = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case UrlConverterDialogFields.MARGIN_TOP:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalUrlConverterPage.getTopDefault();
				}

				pageParameter.top = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case UrlConverterDialogFields.MARGIN_BOTTOM:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalUrlConverterPage.getBottomDefault();
				}

				pageParameter.bottom = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case UrlConverterDialogFields.MARGIN_LEFT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalUrlConverterPage.getLeftDefault();
				}

				pageParameter.left = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case UrlConverterDialogFields.MARGIN_RIGHT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalUrlConverterPage.getRightDefault();
				}

				pageParameter.right = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case UrlConverterDialogFields.UNIT:
				this.setState({
					unit: value
				});
				pageParameter.unit = value;
				break;
			case UrlConverterDialogFields.HEADER_PLAIN:
				parameter.header!.content = this.convertContents(parameter.header!);
				parameter.header!.plain = !value;
				break;
			case UrlConverterDialogFields.HEADER_FONT_FAMILY:
				parameter.header!.fontFamily = value;
				break;
			case UrlConverterDialogFields.HEADER_FONT_SIZE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooter.getFontSizeDefault();
				}

				parameter.header!.fontSize = value;
				break;
			case UrlConverterDialogFields.HEADER_FONT_COLOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.header!.color = value;
				break;
			case UrlConverterDialogFields.HEADER_ALIGNMENT:
				parameter.header!.alignment = value;
				break;
			case UrlConverterDialogFields.HEADER_CONTENT:
				parameter.header!.content = value;
				break;
			case UrlConverterDialogFields.HEADER_MARGIN_TOP:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooterMargins.getTopDefault();
				}

				parameter.header!.margins!.top = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case UrlConverterDialogFields.HEADER_MARGIN_BOTTOM:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooterMargins.getBottomDefault();
				}

				parameter.header!.margins!.bottom = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case UrlConverterDialogFields.HEADER_MARGIN_LEFT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooterMargins.getLeftDefault();
				}

				parameter.header!.margins!.left = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case UrlConverterDialogFields.HEADER_MARGIN_RIGHT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooterMargins.getRightDefault();
				}

				parameter.header!.margins!.right = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case UrlConverterDialogFields.FOOTER_PLAIN:
				parameter.footer!.content = this.convertContents(parameter.footer!);
				parameter.footer!.plain = !value;
				break;
			case UrlConverterDialogFields.FOOTER_FONT_FAMILY:
				parameter.footer!.fontFamily = value;
				break;
			case UrlConverterDialogFields.FOOTER_FONT_SIZE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooter.getFontSizeDefault();
				}

				parameter.footer!.fontSize = value;
				break;
			case UrlConverterDialogFields.FOOTER_FONT_COLOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.footer!.color = value;
				break;
			case UrlConverterDialogFields.FOOTER_ALIGNMENT:
				parameter.footer!.alignment = value;
				break;
			case UrlConverterDialogFields.FOOTER_CONTENT:
				parameter.footer!.content = value;
				break;
			case UrlConverterDialogFields.FOOTER_MARGIN_TOP:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooterMargins.getTopDefault();
				}

				parameter.footer!.margins!.top = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case UrlConverterDialogFields.FOOTER_MARGIN_BOTTOM:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooterMargins.getBottomDefault();
				}

				parameter.footer!.margins!.bottom = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case UrlConverterDialogFields.FOOTER_MARGIN_LEFT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooterMargins.getLeftDefault();
				}

				parameter.footer!.margins!.left = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case UrlConverterDialogFields.FOOTER_MARGIN_RIGHT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooterMargins.getRightDefault();
				}

				parameter.footer!.margins!.right = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-globe small"}/>
					<Translate {...this.props} data="urlconverter.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div className="p-grid">
				<div className="p-col-12 p-xl-8 p-grid divider-right-hidden">
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-grid">
							<div className="p-col-12 p-grid">
								<Button label={Translate.value("buttons.reset")} icon={"icon icon-rotate-270 small"}
										disabled={this.state.submitted} className="reset-button"
										onClick={() => this.resetParameter()}/>
								<Button label={Translate.value("buttons.submit")} disabled={!this.isValid()}
										icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
										className="primary-button" onClick={() => this.submit()}/>
								{this.state.submitted ?
									<Button label={Translate.value("buttons.close")} icon="icon icon-close"
											onClick={() => this.hide()} className="dialog-button"/> :
									<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
											onClick={() => this.hide()} className="dialog-button"/>
								}
							</div>
						</div>
					</fieldset>
				</div>
				<div className="p-col-12 p-xl-4 p-lg-hidden p-grid divider-left-hidden"></div>
			</div>
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let operationData: PortalUrlConverter = this.state.parameter!.data.clone();
		if (FunctionHelper.isEmpty(operationData.basicAuth?.userName) ||
			FunctionHelper.isEmpty(operationData.basicAuth?.password)) {
			operationData.basicAuth = undefined;
		}

		if (FunctionHelper.exists(operationData.header) &&
			FunctionHelper.applyValue(operationData.header?.plain, PortalConverterHeaderFooter.getPlainDefault())) {
			operationData.header!.content = this.convertContents(operationData.header!);
		}

		if (FunctionHelper.exists(operationData.footer) &&
			FunctionHelper.applyValue(operationData.footer?.plain, PortalConverterHeaderFooter.getPlainDefault())) {
			operationData.footer!.content = this.convertContents(operationData.footer!);
		}

		operationData.page!.header!.value = Base64.encode(FunctionHelper.applyValue(
			operationData.header?.content, PortalConverterHeaderFooter.getContentDefault()
		));

		operationData.page!.footer!.value = Base64.encode(FunctionHelper.applyValue(
			operationData.footer?.content, PortalConverterHeaderFooter.getContentDefault()
		));

		let webServiceType: WebServiceType = WebServiceTypes.URLCONVERTER;

		try {
			await this.props.context.session.executeRequest({
				webServiceType: webServiceType,
				operation: UrlConverter.fromJson(operationData.toJson()),
				settings: Settings.fromJson({
					pdfa: {
						pdfaEditProfile: this.props.pdfaEditProfile
					},
					signature: {
						signatureEditProfile: this.props.signatureEditProfile
					},
					compress: {
						compressProfile: this.props.compressProfile,
						compressObjects: {
							objectStreamSize: this.props.objectStreamSize
						}
					}
				} as SettingsInterface)
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.urlconverter.convert", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.hide();
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalUrlConverter = this.state.parameter!.data;

		if (!FunctionHelper.exists(parameter.page)) {
			return;
		}

		let pageParameter: PortalUrlConverterPage = parameter.page as PortalUrlConverterPage;

		this.setState({
			unit: pageParameter.unit || LengthUnit.MILLIMETER,
			defaultParameter: this.state.parameter?.clone()
		});
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.URLCONVERTER;
	}

	protected getDefaultParameter(): Parameter<PortalUrlConverter> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		if (event.key.toLowerCase() === "enter" && (this.state.panel === 1 || this.state.panel === 2)) {
			return;
		}

		await super.handleKeydown(event);
	}
}

const UrlConverterDialog = React.memo(UrlConverterDialogDefinition,
	(
		prevProps: Readonly<UrlConverterDialogProperties>,
		nextProps: Readonly<UrlConverterDialogProperties>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default UrlConverterDialog;